import { PriceBadge } from '@Components/PriceBadge';
import {
    type ApiOrder,
    EnumMaterialFlow,
    EnumOrderType,
    EnumTaxationMode,
    materialFlowOptions,
    orderTypeOptions,
} from '@Model';
import {
    type AsyncStateUpdate,
    Badge,
    Button,
    DateInput,
    FlexRow,
    FloatingCard,
    Grid,
    GridColumns,
    iconSizeSm,
    type IGuiBinder,
    LabeledDiv,
    Select,
    SingleSelect,
    useAutoConfirmed,
    useProp,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { type ComponentProps, memo, useState } from 'react';
import { CompletedAtField } from '../../CompletedAtField';
import { OrderMoreDetails } from './OrderMoreDetails';
import { OrderPartyFields } from './OrderPartyFields';

interface IProps {
    binder: IGuiBinder<ApiOrder>;
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiOrder>;
    canEdit: boolean;

    stateBadges: ComponentProps<typeof Badge>[];

    orderType: EnumOrderType;
    taxationMode: EnumTaxationMode;
    totalNetPrice: number;
    totalGrossPrice: number;
    siteAddress: ApiOrder['site']['address'];
    customerKey: string | undefined;
    pickupSiteKey: string | undefined;
    hasContainerLines: boolean;
    containersBooked: boolean;
}

// @ai-generated 2026-10-22T10:53:17+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const OrderMainSection = memo(function OrderMainSection({
    binder,
    baseApiObjectPath,
    requestUpdateState,
    canEdit,
    stateBadges,
    orderType,
    taxationMode,
    totalNetPrice,
    totalGrossPrice,
    siteAddress,
    customerKey,
    pickupSiteKey,
    hasContainerLines,
    containersBooked,
}: IProps) {
    const i18n = useTranslate('');
    const [showMoreDetails, setShowMoreDetails] = useState(false);

    const materialFlowField = useAutoConfirmed<ApiOrder, EnumMaterialFlow>(binder, 'material_flow');
    const orderTypeField = useAutoConfirmed<ApiOrder, EnumOrderType>(binder, 'type');
    const plannedStartField = useProp(binder, 'planned_start');
    const plannedEndField = useProp(binder, 'planned_end');
    const completedAtField = useProp(binder, 'completed_at');

    const isCashOrder = orderType === EnumOrderType.Cash;

    return (
        <FloatingCard
            title={isCashOrder ? 'Barauftrag' : 'Auftrag'}
            additionalHeaderItems={
                <FlexRow gap="xs">
                    {stateBadges.map((badge, index) => (
                        <Badge key={index} {...badge} />
                    ))}
                </FlexRow>
            }
            collapsible={false}
        >
            <Grid>
                <GridColumns spans={[4, 4, 4]}>
                    <Select<EnumMaterialFlow>
                        items={materialFlowOptions}
                        {...materialFlowField}
                        label={i18n('Stoffweg')}
                    />
                    <SingleSelect<EnumOrderType>
                        items={orderTypeOptions}
                        {...orderTypeField}
                        label={i18n('Auftragstyp')}
                        fullWidth
                    />
                    {isCashOrder && (
                        <LabeledDiv htmlFor="cashAmount" label="Bar-Betrag">
                            <PriceBadge
                                price={taxationMode === EnumTaxationMode.PriceIsGross ? totalGrossPrice : totalNetPrice}
                            />
                        </LabeledDiv>
                    )}
                </GridColumns>
            </Grid>
            <OrderPartyFields
                binder={binder}
                baseApiObjectPath={baseApiObjectPath}
                requestUpdateState={requestUpdateState}
                canEdit={canEdit}
                siteAddress={siteAddress}
                customerKey={customerKey}
                pickupSiteKey={pickupSiteKey}
            />
            <Grid>
                <GridColumns spans={[4, 4, 4]}>
                    <DateInput {...plannedStartField} type="datetime" label="Wunschtermin (von)" />
                    <DateInput {...plannedEndField} type="datetime" label="Wunschtermin (bis)" />
                    <CompletedAtField
                        field={completedAtField}
                        hasContainerLines={hasContainerLines}
                        containersBooked={containersBooked}
                    />
                </GridColumns>
            </Grid>
            <FlexRow justify="start">
                <Button
                    appearance="transparent"
                    size="xs"
                    onClick={() => setShowMoreDetails((shown) => !shown)}
                    aria-expanded={showMoreDetails}
                >
                    {showMoreDetails ? <IconChevronUp size={iconSizeSm} /> : <IconChevronDown size={iconSizeSm} />}
                    &nbsp;{i18n('Weitere Angaben')}
                </Button>
            </FlexRow>
            {showMoreDetails && (
                <OrderMoreDetails
                    binder={binder}
                    baseApiObjectPath={baseApiObjectPath}
                    requestUpdateState={requestUpdateState}
                />
            )}
        </FloatingCard>
    );
});
